import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import Chart from 'react-apexcharts';
import moment from 'moment';
import classes from './growthBySpecializations.module.scss';
import GrowthBySpecializationsFilters from './GrowthBySpecializationsFilters/GrowthBySpecializationsFilters';
import { useQuery } from '../../../../utils/hooks/useQuery';
import FilterButton from '../../../_common/FilterButton/FilterButton';
import { getDashboardGrowthBySpecializationsRequest } from '../../../../store/actions/dashboard';
import utils from '../../../../utils/utils';
import allergyIcon from '../../../../assets/icons/allergy_ellipse-green.svg';
import assistedLivingIcon from '../../../../assets/icons/assistedLiving_square-red.svg';
import cardiologyIcon from '../../../../assets/icons/cardiology_ellipse-blueBorder.svg';
import dermatologyIcon from '../../../../assets/icons/dermatology_triangle-pink.svg';
import endocrinologyIcon from '../../../../assets/icons/endocrinology_ellipse-lightBlue.svg';
import gastroenterologyIcon from '../../../../assets/icons/gastroenterology_square-green.svg';
import hepatitisIcon from '../../../../assets/icons/hepatitsC_triangle-orange.svg';
import homeHealthIcon from '../../../../assets/icons/homeHealth_triangle-purple.svg';
import hospiceIcon from '../../../../assets/icons/hospice_ellipse-grey.svg';
import hospitalIcon from '../../../../assets/icons/hospital_ellipse-greenBorder.svg';
import infectiousDiseaseIcon from '../../../../assets/icons/infectiousDisease_ellipse-red.svg';
import internalMedicineIcon from '../../../../assets/icons/internalMedicine_ellipse-turquoise.svg';
import nephrologyIcon from '../../../../assets/icons/nephrology_triangle-yellow.svg';
import neurologyIcon from '../../../../assets/icons/neurology_triangle-pink.svg';
import oncologyIcon from '../../../../assets/icons/oncology_ellipse-grey.svg';
import orthopedicIcon from '../../../../assets/icons/orthopedic_triangle-orange.svg';
import pulmonaryIcon from '../../../../assets/icons/pulmonaryClinic_square-turquoise.svg';
import residentialLivingIcon from '../../../../assets/icons/residentialLiving_ellipse-yellowBorder.svg';
import rheumatologyIcon from '../../../../assets/icons/rheumatology_triangle-ligthGreen.svg';
import transplantIcon from '../../../../assets/icons/transplant_triangle-grey.svg';
import urologyIcon from '../../../../assets/icons/urology_ellipse-pinkBorder.svg';
import skilledNursingCenterIcon from '../../../../assets/icons/skilledNursingCenter_ellipse-blue.svg';
import Loader from '../../../_common/Loader/Loader';

const typeData = [
  {
    title: 'Revenue',
    key: 'revenue',
    value: 'revenue',
  },
  {
    title: 'Scripts',
    key: 'scripts',
    value: 'scripts',
  },
];

const specType = [
  {
    name: 'Allergy',
    color: '#00C974',
    image: allergyIcon,
    figure: 'ellipse',
  },
  {
    name: 'Assisted Living',
    color: '#FF0000',
    image: assistedLivingIcon,
    figure: 'square',
  },
  {
    name: 'Cardiology',
    color: '#405DF9',
    image: cardiologyIcon,
    figure: 'ellipse',
  },
  {
    name: 'Dermatology',
    color: '#ED589D',
    image: dermatologyIcon,
    figure: 'triangle',
  },
  {
    name: 'Endocrinology',
    color: '#3AACFF',
    image: endocrinologyIcon,
    figure: 'ellipse',
  },
  {
    name: 'Gastroenterology',
    color: '#00C974',
    image: gastroenterologyIcon,
    figure: 'square',
  },
  {
    name: 'Hepatology',
    color: '#FFA338',
    image: hepatitisIcon,
    figure: 'triangle',
  },
  {
    name: 'Home Health',
    color: '#855CF8',
    image: homeHealthIcon,
    figure: 'triangle',
  },
  {
    name: 'Hospice',
    color: '#C0C0C0',
    image: hospiceIcon,
    figure: 'ellipse',
  },
  {
    name: 'Hospital',
    color: '#4f8a18',
    image: hospitalIcon,
    figure: 'ellipse',
  },
  {
    name: 'Infectious Disease',
    color: '#FF0000',
    image: infectiousDiseaseIcon,
    figure: 'ellipse',
  },
  {
    name: 'Internal Medicine',
    color: '#0EC9E2',
    image: internalMedicineIcon,
    figure: 'ellipse',
  },
  {
    name: 'Nephrology',
    color: '#FFD338',
    image: nephrologyIcon,
    figure: 'triangle',
  },
  {
    name: 'Neurology',
    color: '#E905E0',
    image: neurologyIcon,
    figure: 'triangle',
  },
  {
    name: 'Oncology',
    color: '#808DAF',
    image: oncologyIcon,
    figure: 'ellipse',
  },
  {
    name: 'Orthopedic',
    color: '#FFA800',
    image: orthopedicIcon,
    figure: 'triangle',
  },
  {
    name: 'Pulmonology',
    color: '#A6CEE3',
    image: pulmonaryIcon,
    figure: 'square',
  },
  {
    name: 'Residential Living',
    color: '#FFD338',
    image: residentialLivingIcon,
    figure: 'ellipse',
  },
  {
    name: 'Rheumatology',
    color: '#05E9CD',
    image: rheumatologyIcon,
    figure: 'triangle',
  },
  {
    name: 'Skilled Nursing Center',
    color: '#547EEE',
    image: skilledNursingCenterIcon,
    figure: 'ellipse',
  },
  {
    name: 'Transplant',
    color: '#C0C0C0',
    image: transplantIcon,
    figure: 'triangle',
  },
  {
    name: 'Urology',
    color: '#D90B9F',
    image: urologyIcon,
    figure: 'ellipse',
  },
];

const chartTemplate = {
  series: [],

  options: {
    chart: {
      height: 350,
      type: 'line',
      toolbar: {
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },

    colors: [],

    stroke: {
      curve: 'straight',
      width: 2,
    },

    tooltip: {
      shared: true,
      style: {
        fontSize: '12px',
        fontFamily: 'Inter, sans-serif',
      },
      y: {
        formatter(val) {
          const formatter = Intl.NumberFormat('en');
          return formatter.format(val);
        },
      },
    },

    dataLabels: {
      enabled: false,
    },

    plotOptions: {
      bar: {
        columnWidth: '20%',
      },
    },

    annotations: {
      points: [],
    },

    markers: {
      size: 0,
      colors: ['transparent'],
      strokeWidth: 1,
      shape: ['circle'], // "circle" | "square" | "rect"
      hover: {
        sizeOffset: 0,
      },
    },
    grid: {
      borderColor: 'rgba(0, 0, 0, 0.1)',
      xaxis: {
        lines: {
          show: true,
        },
      },

      yaxis: {
        lines: {
          show: true,
        },
      },
    },

    legend: {
      show: false,
    },

    yaxis: {
      showForNullSeries: false,
      labels: {
        style: {
          colors: ['rgba(29, 39, 64, 0.5)'],
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          cssClass: 'apexcharts-yaxis-label',
        },

        formatter(val) {
          const formatter = Intl.NumberFormat('en', { notation: 'compact' });
          return formatter.format(val);
        },
      },
    },

    xaxis: {
      categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      labels: {
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          cssClass: 'apexcharts-xaxis-label',
        },
      },
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      crosshairs: {
        fill: {
          type: 'gradient',
          gradient: {
            colorFrom: '#D8E3F0',
            colorTo: '#BED1E6',
            stops: [0, 100],
            opacityFrom: 0.4,
            opacityTo: 0.5,
          },
        },
      },
      tooltip: {
        enabled: false,
      },
    },
  },
};

let firstLoad = true;

const GrowthBySpecializations = () => {
  const { query, setQuery } = useQuery();
  const dispatch = useDispatch();
  const { dashboardGrowthBySpecializations, statusRequest } = useSelector((store) => store.dashboard);

  const [chart, setChart] = useState(chartTemplate);
  const [requestData, setRequestData] = useState({
    start: query.start || '',
    end: query.end || '',
    product: query.product || '',
    state: query.state || '',
    rep: query.rep || '',
  });
  const [chartType, setChartType] = useState(query.chartType || 'revenue');
  const [chartLoading, setChartsLoading] = useState(false);

  const [currentCheckedTypes, setCurrentCheckedTypes] = useState([]);
  const [currentTypes, setCurrentTypes] = useState([]);

  useEffect(() => {
    const data = utils.deleteEmptyKeys(requestData);

    dispatch(getDashboardGrowthBySpecializationsRequest(data));

    return () => firstLoad = true;
  }, []);

  useLayoutEffect(() => {
    (async () => {
      setChartsLoading((prev) => !prev);
      const newChart = { ...chartTemplate };
      const filteredData = dashboardGrowthBySpecializations.filter((d) => d.specialization);
      const types = filteredData.map((d) => d.specialization)
        .filter((d) => specType.find((s) => s.name === d));

      if (firstLoad && !_.isEmpty(dashboardGrowthBySpecializations)) {
        setCurrentCheckedTypes(types.slice(0, 3));
        setCurrentTypes(types);
        firstLoad = false;
        setChartsLoading((prev) => !prev);
        return;
      }

      const groupData = _.groupBy(filteredData, 'specialization');

      const filteredTypes = specType.filter((t) => currentCheckedTypes.includes(t.name));

      if (_.isEmpty(dashboardGrowthBySpecializations)) {
        newChart.series = [{
          name: '',
          data: [],
        }];
      } else {
        newChart.series = filteredTypes
          .map((t) => ({
            name: t.name,
            data: chart.options.xaxis.categories.map((c) => (groupData[t.name].find((d) => moment(d.start_date).format('MMM') === c)
              ? groupData[t.name].find((d) => moment(d.start_date).format('MMM') === c)[chartType]
              : 0)),
          }));

        newChart.options.colors = filteredTypes.map((t) => t.color);
      }

      const points = [];

      newChart.series.forEach((t, index) => t.data.forEach((d, i) => {
        const thisType = filteredTypes[index];
        const figureSize = thisType.figure === 'triangle' ? 15 : thisType.figure === 'square' ? 12 : 13;

        if (d) {
          points.push({
            x: chart.options.xaxis.categories[i],
            y: d,
            marker: {
              size: 0,
            },
            image: {
              path: thisType.image,
              width: figureSize,
              height: figureSize,
              offsetX: 0,
              offsetY: 0,
            },
          });
        }
      }));

      newChart.options.annotations.points = points;

      await setChart(newChart);

      setChartsLoading((prev) => !prev);
    })();
  }, [currentCheckedTypes, dashboardGrowthBySpecializations, chartType]);

  const updateFilters = async () => {
    const data = utils.deleteEmptyKeys({ ...requestData, chartType });

    await dispatch(getDashboardGrowthBySpecializationsRequest({ ...data }));

    setQuery(data);

    firstLoad = true;
  };

  const clearFilters = () => {
    setRequestData({
      product: '',
      state: '',
      rep: '',
    });

    setQuery({});
    setChartType('revenue');

    dispatch(getDashboardGrowthBySpecializationsRequest({}));
    firstLoad = true;
  };

  const selectType = (type) => {
    setCurrentCheckedTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((p) => p !== type);
      }

      return [...prev, type];
    });
  };

  return (
    <div className={classes.wrapper}>
      <GrowthBySpecializationsFilters
        setFilterData={setRequestData}
        filtersData={requestData}
        updateFilters={updateFilters}
        clearFilters={clearFilters}
      />

      <div className={classes.dashboard_growth_wrapper}>
        <div className={classes.dashboard_growth_left_block}>
          <div className={classes.chart_type_wrapper}>
            <FilterButton
              onClick={setChartType}
              activeValue={chartType}
              buttonsData={typeData}
              height={40}
              fontWeight={600}
            />
          </div>

          <div className={classes.chart_wrapper}>
            {!chartLoading && statusRequest === 'success'
              ? (chart.series?.[0]?.data?.length
                ? (
                  <Chart
                    options={chart.options}
                    series={chart.series}
                    width="100%"
                    height={500}
                  />
                )

                : (
                  <div className={classes.chart_is_empty}>No data to display</div>
                ))

              : <Loader size={40} className={classes.chart_wrapper_loader} />}
          </div>
        </div>

        <div className={classes.dashboard_growth_right_block}>
          {!firstLoad && currentTypes.map((o) => {
            const {
              name, color, image, figure,
            } = specType.find((t) => t.name === o);

            return (
              <div
                key={name}
                className={`${classes.dashboard_growth_type_wrapper} ${!currentCheckedTypes.includes(name)
                  ? classes.disable
                  : ''}`}
                onClick={() => selectType(name)}
              >
                <div
                  style={{ background: color }}
                  className={`${classes.dashboard_growth_type_icon} ${figure === 'triangle'
                    ? classes.triangle : figure === 'square' ? classes.square : ''}`}
                >
                  <img src={image} alt="icon" />
                </div>

                <div className={classes.dashboard_growth_type_name}>
                  {name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GrowthBySpecializations;
